import type { DayCountConvention, FigureStatus, ISODateString, Loan, LoanEvent, RepaymentArrangement, UUID } from '../../types';
import {
  addDays,
  addMonthsClamped,
  firstEmiDateStrictlyAfter,
  isAfter,
  isAfterOrEqual,
  isBefore,
  isBeforeOrEqual,
  wholeMonthsBetween,
} from './dates';
import { applyEmiRounding, calculateEmi } from './emi';

/**
 * ============================================================================
 * CALCULATION ENGINE (Phase 2)
 * ============================================================================
 *
 * The single, real calculation engine — source of truth for all derived
 * loan state (Schedule, Dashboard, Analytics, Reports, and the
 * repayment-consequence suggestion UI). Types below were locked in
 * Phase 1; `computeSchedule()` is now fully implemented against them
 * without needing to change the contract.
 *
 * Core design: a single day-by-day walk over the loan's life (see
 * computeSchedule() below) resolves outstanding principal and the
 * applicable interest rate for every calendar day from stored events,
 * then rolls consecutive days into EMI cycles. This is what makes staged
 * disbursement, pre-EMI, mid-cycle rate changes, and mid-cycle
 * prepayments all fall out of one mechanism rather than needing
 * separate special-case code paths per feature.
 */

/**
 * A single derived day of the loan's life. This is the engine's true
 * computational granularity (spec §9/§15 fixed decision #5: "logical
 * daily granularity... derived efficiently", not persisted wholesale).
 * Phase 2 computes arrays of these on demand; nothing in Phase 1 stores
 * them.
 */
export interface ScheduleDay {
  date: ISODateString;
  outstandingPrincipal: number;
  applicableAnnualRatePercent: number;
  dailyInterestAccrued: number;
  /** Event ids that determined this day's principal/rate (for auditability/debugging). */
  drivingEventIds: UUID[];
}

/**
 * Which repayment phase a due date belongs to.
 *  - 'pre_emi' : interest-only payment (pre-EMI interest) on the outstanding
 *                DISBURSED principal. Principal is not reduced by it.
 *  - 'regular' : principal-and-interest EMI; principal declines.
 * Both are CALCULATED schedule rows, never recorded payments.
 */
export type RepaymentPhase = 'pre_emi' | 'regular';

export type ArrangementSource = 'user_selected' | 'working_assumption';

/**
 * The repayment behaviour the calculations use. LIC HFL's exact allocation
 * is not known, so unless the user chooses otherwise this is a WORKING
 * ASSUMPTION (source 'working_assumption'), editable via Loan Settings:
 * the Regular EMI Start Date and/or the arrangement choice.
 */
export interface ResolvedArrangement {
  mode: Exclude<RepaymentArrangement, 'unconfirmed'>;
  source: ArrangementSource;
  /**
   * Regular EMI applies from this date (the first regular EMI is the first
   * EMI date on or after it). Earlier EMI dates are interest-only.
   * Undefined => regular EMI from the first EMI date.
   */
  startDate?: ISODateString;
  /** 'saved' = Loan.regularEmiStartDate; 'automatic' = derived by the working assumption. */
  startDateSource: 'saved' | 'automatic' | 'none';
  /**
   * How a SAVED Regular EMI Start Date is read (only when startDateSource is 'saved'):
   *  - 'first_regular_emi_after_date': the date keeps the meaning it has always had
   *    in this app — regular EMI begins with the first EMI date strictly AFTER it,
   *    so a payment due ON the date is still interest-only. Used for Automatic
   *    (including a stored "unconfirmed") so saved records are not reinterpreted.
   *  - 'regular_emi_from_date': the user explicitly chose a repayment assumption,
   *    so regular EMI applies FROM the date (an EMI date on it is regular).
   * `startDate` above is always the resulting INCLUSIVE start.
   */
  savedDateRule?: 'first_regular_emi_after_date' | 'regular_emi_from_date';
}

/**
 * Where the regular EMI amount used for a cycle came from. Surfaced so
 * the UI never presents an assumed figure as a recorded one.
 *  - 'recorded_event'             : a repayment consequence (rate change /
 *                                   prepayment / balance correction) set it.
 *  - 'recorded_loan'              : Loan.regularEmiAmount (known/entered).
 *  - 'original_full_disbursement' : Loan.originalEmiAtFullDisbursement,
 *                                   used ONLY when disbursements to date
 *                                   have reached the approved amount (i.e.
 *                                   the EMI was computed on this principal).
 *  - 'derived_over_contract_tenure': ASSUMPTION — the standard EMI formula
 *                                   applied to the outstanding principal over
 *                                   the instalments remaining to contractual
 *                                   maturity. Not lender-confirmed.
 */
export type RegularEmiBasis =
  | 'recorded_event'
  | 'recorded_loan'
  | 'original_full_disbursement'
  | 'derived_over_contract_tenure';

/**
 * A derived, presentation-layer rollup of consecutive ScheduleDay entries
 * between two EMI dates. This is what the Schedule screen's month rows
 * and the Report's tables render. Each row is one scheduled due date;
 * `phase` says whether it is an interest-only pre-EMI payment or a
 * regular EMI. A row is a CALCULATED/scheduled figure — it is never a
 * confirmed historical payment merely because its date has passed.
 */
export interface ScheduleMonth {
  emiDate: ISODateString;
  /** Phase of this scheduled payment (see RepaymentPhase). A calculated row, not a recorded payment. */
  phase: RepaymentPhase;
  openingBalance: number;
  closingBalance: number;
  /** The scheduled instalment payable on this date (interest-only for a pre-EMI row). Excludes prepayments. */
  emiAmount: number;
  /** Interest actually covered by this row's payment (including any previously carried-forward unpaid interest). */
  interestComponent: number;
  /** Interest accrued over this cycle's days (before any carry-forward). Equals interestComponent for an ordinary pay-as-you-go cycle. */
  accruedInterest: number;
  /** Principal reduced by this row's instalment. Always 0 for a pre-EMI row. */
  principalComponent: number;
  prepaymentAmount: number;
  /** Disbursements (and financed charges) that took effect during this cycle. A disbursement is NOT a repayment. */
  disbursedInCycle: number;
  /** Accrued interest not covered by the payment, carried forward as UNPAID interest (neither paid nor silently capitalised). */
  unpaidInterestCarriedForward: number;
  /** For a regular row: where the regular EMI amount came from. Undefined for pre-EMI rows. */
  regularEmiBasis?: RegularEmiBasis;
  cumulativeInterest: number;
  cumulativePrincipal: number;
  cumulativeTotalPayment: number;
  isReconciled: boolean;
  reconciledFromEventId?: UUID;
  sourceEventIds: UUID[];
  /** How this month's key figures should be labelled in the UI. */
  status: FigureStatus;
  /**
   * True when this cycle's payment was CAPPED below the normally
   * resolved EMI specifically because paying the full resolved EMI
   * would have overshot the remaining balance (i.e. this cycle closes
   * the loan with a smaller, mechanically-generated final payment,
   * NOT a genuine contractual installment at the regular EMI amount).
   *
   * Set ONLY by that one condition — never inferred from a cycle's
   * payment merely differing from the regular EMI for some other reason.
   * Consumers that count regular repayment cycles MUST exclude any cycle
   * with this flag true (see isGenuineRegularCycle()).
   */
  isResidualClosingPayment: boolean;
}

/** A regular (non-residual) EMI cycle — the unit counted as "tenure". Pre-EMI rows are never counted. */
export function isGenuineRegularCycle(m: ScheduleMonth): boolean {
  return m.phase === 'regular' && !m.isResidualClosingPayment;
}

/**
 * A visible, explicit assumption behind a projection. Structured (not
 * pre-formatted text) so every screen and the PDF can render the same
 * assumption identically (see projectionText.ts).
 */
export type ProjectionAssumption =
  | { code: 'undisbursed_never_disbursed'; amount: number }
  | { code: 'emi_recorded' }
  | { code: 'emi_original_full_disbursement' }
  | {
      code: 'emi_derived_over_contract_tenure';
      emi: number;
      instalments: number;
      maturityDate: ISODateString;
      maturitySource: 'lender_reported' | 'derived_from_tenure';
    }
  | { code: 'arrangement'; mode: ResolvedArrangement['mode']; source: ArrangementSource; startDate?: ISODateString; startDateSource: ResolvedArrangement['startDateSource']; firstRegularDueDate?: ISODateString }
  | { code: 'rate_unchanged'; ratePercent: number }
  | { code: 'no_further_prepayments' }
  | { code: 'payments_on_due_dates' }
  | { code: 'day_count'; convention: DayCountConvention };

/**
 * Whether a closure date / remaining tenure / lifetime-interest figure can
 * honestly be shown. 'unavailable' means the inputs needed for an
 * identifiable repayment scenario are missing — consumers MUST then show
 * the explanation instead of a date. 'modelled' means the figures follow
 * from the listed assumptions; it never means lender-confirmed.
 */
export interface ProjectionInfo {
  status: 'unavailable' | 'modelled';
  unavailableReason?: 'no_disbursement' | 'does_not_amortise';
  assumptions: ProjectionAssumption[];
}

/**
 * The full output of a schedule computation — daily figures (usually not
 * rendered directly, but available for drill-down/debugging), the
 * derived monthly rollup, and some top-line summary figures that Home/
 * Analytics will consume without needing to re-derive them.
 */
export interface ComputedSchedule {
  months: ScheduleMonth[];
  /**
   * Present only when explicitly requested (see ComputeScheduleOptions);
   * omitted by default to avoid needless computation/memory use for
   * screens that only need the monthly rollup.
   */
  days?: ScheduleDay[];
  summary: ScheduleSummary;
}

export interface ScheduleSummary {
  /**
   * Sum of ALL scheduled instalment payments in `months` — regular EMIs
   * AND interest-only pre-EMI payments (see totalPreEmiPaid for the
   * pre-EMI subset). Scheduled, not confirmed, payments.
   */
  totalEmiPaid: number;
  /** The interest-only (pre-EMI) subset of totalEmiPaid. */
  totalPreEmiPaid: number;
  totalInterestPaid: number;
  totalPrincipalRepaid: number;
  totalPrepayments: number;
  /** Sum of recorded disbursements effective on or before the end of this walk. */
  totalDisbursed: number;
  /**
   * Principal per the walk INCLUDING calculated (scheduled) instalment
   * repayments up to the end date. An estimate once any scheduled
   * principal repayment has fallen due.
   */
  currentOutstandingPrincipal: number;
  /**
   * Principal per ACTUAL RECORDED events only (disbursements, financed
   * charges, prepayments, balance corrections). Scheduled instalment
   * repayments never reduce it.
   */
  currentRecordedPrincipal: number;
  /**
   * Modelled closure date. Present ONLY when the walk actually reached a
   * zero balance under an identifiable scenario — never for a loan whose
   * regular repayment has not been recorded as commenced.
   */
  projectedClosureDate?: ISODateString;
  projectedTotalInterest?: number;
  projectedTotalPayment?: number;
  isLoanClosed: boolean;
  /**
   * The interest rate applicable as of the end of this computation's
   * walk (i.e. as of `asOfDate` when supplied, otherwise as of the
   * trajectory's natural closure/walk end).
   */
  currentApplicableAnnualRatePercent: number;
  /**
   * The REGULAR (principal + interest) EMI that applies — or, before
   * regular repayment has commenced, would apply — as of the end of this
   * walk. During pre-EMI this is NOT a payable amount. `regularEmiBasis`
   * states whether it is recorded or assumed. Never conflate with
   * Loan.originalEmiAtFullDisbursement.
   */
  currentApplicableEmi: number;
  regularEmiBasis?: RegularEmiBasis;
  /** Repayment phase as of the end of this walk. */
  currentPhase: 'pre_emi' | 'regular' | 'closed';
  /** How payments are allocated for this computation, and where that came from. */
  arrangement: ResolvedArrangement;
  /** The next scheduled due date after the end of this walk (undefined once closed). */
  nextDueDate?: ISODateString;
  /** The first EMI due date ON OR AFTER regularEmiStartDate (undefined if no start date is set). */
  firstRegularEmiDueDate?: ISODateString;
  /** Pre-EMI interest added to principal under the explicit 'capitalize_at_emi_start' rule (0 for pay-as-you-go). */
  preEmiInterestCapitalised: number;
  /** Accrued interest currently unpaid and carried forward (neither paid nor capitalised). */
  unpaidInterestCarried: number;
  projection: ProjectionInfo;
}

/**
 * Input to computeSchedule(). All events for the loan are passed in;
 * the engine itself decides how to interpret each type. `asOfDate` bounds
 * how far forward to project (defaults to loan closure or a sane cap).
 * `includeDailyBreakdown` controls whether `ComputedSchedule.days` is
 * populated (expensive; off by default).
 */
export interface ComputeScheduleOptions {
  loan: Loan;
  events: LoanEvent[];
  asOfDate?: ISODateString;
  includeDailyBreakdown?: boolean;
}

/**
 * ============================================================================
 * INTERNAL: per-day principal/rate resolution
 * ============================================================================
 * These are pure lookups against the (chronologically sorted) event
 * lists — no mutation, callable repeatedly for any date. Kept as small,
 * separately-testable functions rather than inlined into the main walk,
 * so each piece of "how do we resolve X for a given day" is independently
 * verifiable against the hand-calculable test cases.
 */

interface SortedEvents {
  disbursements: Extract<LoanEvent, { type: 'disbursement' }>[];
  rateChanges: Extract<LoanEvent, { type: 'rate_change' }>[];
  prepayments: Extract<LoanEvent, { type: 'prepayment' }>[];
  balanceCorrections: Extract<LoanEvent, { type: 'balance_correction' }>[];
  charges: Extract<LoanEvent, { type: 'charge' }>[];
  emiDateChanges: Extract<LoanEvent, { type: 'emi_date_change' }>[];
}

/**
 * Splits the flat active-event list into per-type arrays, each sorted
 * ascending by effectiveDate (ties broken by createdAt ascending — the
 * order they were actually entered). This ascending order is the
 * opposite of eventsRepo.listEvents()'s descending Timeline-display
 * order; the engine always re-sorts itself rather than depending on the
 * repo's ordering convention, since the two have different needs.
 */
function sortEvents(events: LoanEvent[]): SortedEvents {
  const byDateAsc = (a: LoanEvent, b: LoanEvent) => {
    if (a.effectiveDate !== b.effectiveDate) return a.effectiveDate < b.effectiveDate ? -1 : 1;
    return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
  };
  const active = events.filter((e) => !e.supersededAt);
  return {
    disbursements: active.filter((e): e is Extract<LoanEvent, { type: 'disbursement' }> => e.type === 'disbursement').sort(byDateAsc),
    rateChanges: active.filter((e): e is Extract<LoanEvent, { type: 'rate_change' }> => e.type === 'rate_change').sort(byDateAsc),
    prepayments: active.filter((e): e is Extract<LoanEvent, { type: 'prepayment' }> => e.type === 'prepayment').sort(byDateAsc),
    balanceCorrections: active
      .filter((e): e is Extract<LoanEvent, { type: 'balance_correction' }> => e.type === 'balance_correction')
      .sort(byDateAsc),
    charges: active.filter((e): e is Extract<LoanEvent, { type: 'charge' }> => e.type === 'charge').sort(byDateAsc),
    emiDateChanges: active
      .filter((e): e is Extract<LoanEvent, { type: 'emi_date_change' }> => e.type === 'emi_date_change')
      .sort(byDateAsc),
  };
}

/**
 * The applicable annual interest rate for a given day: the latest
 * RateChangeEvent with effectiveDate <= day, falling back to the loan's
 * original rate if no rate change has occurred yet by that day.
 */
function resolveRateForDay(day: ISODateString, loan: Loan, sorted: SortedEvents): number {
  let rate = loan.originalAnnualRatePercent;
  for (const rc of sorted.rateChanges) {
    if (isAfter(rc.effectiveDate, day)) break; // sorted ascending; no later event can apply either
    rate = rc.annualRatePercent;
  }
  return rate;
}

/** The applicable EMI day-of-month for a given day, from EmiDateChangeEvents (falls back to loan.emiDayOfMonth). */
function resolveEmiDayOfMonthForDay(day: ISODateString, loan: Loan, sorted: SortedEvents): number {
  let emiDay = loan.emiDayOfMonth;
  for (const ec of sorted.emiDateChanges) {
    if (isAfter(ec.effectiveDate, day)) break;
    emiDay = ec.newEmiDayOfMonth;
  }
  return emiDay;
}

/**
 * An EMI recorded EXPLICITLY — never assumed. Resolution order:
 *  1. The latest RepaymentConsequence.newEmi whose own
 *     repaymentTermsEffectiveDate <= day (NOT the causing event's
 *     effectiveDate — those can differ). Consequences from rate changes,
 *     prepayments and balance corrections are merged and sorted together
 *     so "most recent wins" applies uniformly.
 *  2. Loan.regularEmiAmount, if the user has recorded a known regular EMI.
 * Returns undefined when nothing explicit exists; the caller then applies
 * the labelled fallbacks (see resolveRegularEmi in computeSchedule).
 *
 * CHANGE vs. the original engine: this no longer silently falls back to
 * loan.originalEmiAtFullDisbursement. That fallback applied the EMI of
 * the FULL sanctioned amount to a partially disbursed principal, which
 * is what produced an unsupported accelerated-closure forecast.
 */
function resolveExplicitEmi(
  day: ISODateString,
  loan: Loan,
  sorted: SortedEvents
): { amount: number; basis: 'recorded_event' | 'recorded_loan' } | undefined {
  const allConsequences: { effectiveDate: ISODateString; newEmi?: number }[] = [];
  for (const rc of sorted.rateChanges) {
    allConsequences.push({
      effectiveDate: rc.repaymentConsequence.repaymentTermsEffectiveDate,
      newEmi: rc.repaymentConsequence.newEmi,
    });
  }
  for (const pp of sorted.prepayments) {
    if (pp.repaymentConsequence) {
      allConsequences.push({
        effectiveDate: pp.repaymentConsequence.repaymentTermsEffectiveDate,
        newEmi: pp.repaymentConsequence.newEmi,
      });
    }
  }
  for (const bc of sorted.balanceCorrections) {
    allConsequences.push({
      effectiveDate: bc.repaymentConsequence.repaymentTermsEffectiveDate,
      newEmi: bc.repaymentConsequence.newEmi,
    });
  }
  allConsequences.sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0));

  let emi: number | undefined;
  for (const c of allConsequences) {
    if (isAfter(c.effectiveDate, day)) break;
    if (c.newEmi !== undefined) emi = c.newEmi;
  }
  if (emi !== undefined) return { amount: emi, basis: 'recorded_event' };
  if (loan.regularEmiAmount !== undefined && loan.regularEmiAmount > 0) {
    return { amount: loan.regularEmiAmount, basis: 'recorded_loan' };
  }
  return undefined;
}

/**
 * Resolves the repayment behaviour WITHOUT altering stored data.
 *
 * WORKING ASSUMPTION (used unless the user chooses otherwise): the sanction
 * letter charges pre-EMI interest from the date of disbursement to the date
 * EMI commences, so for a loan that is NOT yet fully disbursed when its first
 * EMI date arrives, that first payment is treated as interest-only and
 * regular principal-and-interest EMIs start on the following EMI date. For a
 * loan fully disbursed by its first EMI date, regular EMIs start on the
 * first EMI date. A saved Regular EMI Start Date always overrides the
 * automatic date. LIC HFL has not confirmed this; it is editable.
 */
export function resolveRepaymentArrangement(loan: Loan, events: LoanEvent[]): ResolvedArrangement {
  const stored = loan.repaymentArrangement;
  const saved = loan.regularEmiStartDate;
  if (stored === 'regular_from_start') {
    return {
      mode: 'regular_from_start',
      source: 'user_selected',
      startDate: saved,
      startDateSource: saved ? 'saved' : 'none',
      savedDateRule: saved ? 'regular_emi_from_date' : undefined,
    };
  }
  const source: ArrangementSource = stored === 'interest_only_then_regular' ? 'user_selected' : 'working_assumption';
  if (saved) {
    // Explicit choice: regular EMI applies FROM the saved date. Automatic / stored "unconfirmed": the saved
    // date keeps its original meaning (first regular EMI = first EMI date strictly AFTER it), so a record
    // saved before this model existed is never reinterpreted.
    const explicitFrom = stored === 'interest_only_then_regular';
    return {
      mode: 'interest_only_then_regular',
      source,
      startDate: explicitFrom ? saved : addDays(saved, 1),
      startDateSource: 'saved',
      savedDateRule: explicitFrom ? 'regular_emi_from_date' : 'first_regular_emi_after_date',
    };
  }

  const sorted = sortEvents(events);
  const first = sorted.disbursements[0]?.effectiveDate;
  if (!first) return { mode: 'interest_only_then_regular', source, startDate: undefined, startDateSource: 'none' };
  const firstDue = firstEmiDateStrictlyAfter(first, resolveEmiDayOfMonthForDay(first, loan, sorted));
  const fullyDisbursedByFirstDue = totalDisbursedUpTo(firstDue, sorted) >= loan.approvedAmount - 0.005;
  return {
    mode: 'interest_only_then_regular',
    source,
    startDate: fullyDisbursedByFirstDue ? first : addDays(firstDue, 1),
    startDateSource: 'automatic',
  };
}

/** First EMI due date on or after `date` (a due date equal to `date` qualifies). */
function firstEmiDateOnOrAfter(date: ISODateString, emiDayOfMonth: number): ISODateString {
  return firstEmiDateStrictlyAfter(addDays(date, -1), emiDayOfMonth);
}

/** Total of active disbursement events effective on or before `day` (an actual-events figure; never includes the undisbursed remainder). */
function totalDisbursedUpTo(day: ISODateString, sorted: SortedEvents): number {
  let total = 0;
  for (const d of sorted.disbursements) {
    if (isAfter(d.effectiveDate, day)) break;
    total += d.amount;
  }
  return total;
}

export interface ContractualMaturity {
  date: ISODateString;
  /** 'lender_reported' = entered from LIC HFL; 'derived_from_tenure' = this app's own derivation (an estimate, not a lender figure). */
  source: 'lender_reported' | 'derived_from_tenure';
}

/**
 * The contractual loan maturity. Prefers the lender-reported date. Else
 * DERIVES it as: the first EMI due date strictly after the first
 * disbursement, plus (originalTenureMonths - 1) months — i.e. the
 * contractual tenure is counted as `originalTenureMonths` monthly due
 * dates starting from the first due date after first disbursement. This
 * derivation is an assumption (labelled 'derived_from_tenure'); for the
 * observed LIC HFL loan it reproduces the reported 7 Oct 2051, but that
 * is a consistency check, not proof of LIC's rule.
 */
export function getContractualMaturity(loan: Loan, events: LoanEvent[]): ContractualMaturity | undefined {
  if (loan.lenderReported?.contractualMaturityDate) {
    return { date: loan.lenderReported.contractualMaturityDate, source: 'lender_reported' };
  }
  const sorted = sortEvents(events);
  const first = sorted.disbursements[0]?.effectiveDate ?? loan.loanStartDate;
  if (!first) return undefined;
  const firstDue = firstEmiDateStrictlyAfter(first, resolveEmiDayOfMonthForDay(first, loan, sorted));
  return { date: addMonthsClamped(firstDue, Math.max(0, loan.originalTenureMonths - 1)), source: 'derived_from_tenure' };
}

/** Number of monthly instalments from `dueDate` to `maturity` inclusive (minimum 1). */
function instalmentsRemainingFrom(dueDate: ISODateString, maturity: ISODateString): number {
  return Math.max(1, wholeMonthsBetween(dueDate, maturity) + 1);
}

/**
 * Instalments remaining from the first EMI due date strictly after
 * `afterDate` through contractual maturity (inclusive), or undefined when
 * no maturity can be determined. Shared so the rate-only savings baseline
 * uses the same contractual horizon as the assumed-EMI scenario.
 */
export function remainingInstalmentsToMaturity(loan: Loan, events: LoanEvent[], afterDate: ISODateString): number | undefined {
  const maturity = getContractualMaturity(loan, events);
  if (!maturity) return undefined;
  const sorted = sortEvents(events);
  const nextDue = firstEmiDateStrictlyAfter(afterDate, resolveEmiDayOfMonthForDay(afterDate, loan, sorted));
  return instalmentsRemainingFrom(nextDue, maturity.date);
}

/**
 * The next scheduled payment strictly after `asOfDate`, from an already
 * computed schedule. A due date that has passed is still just a
 * scheduled/estimated payment — the app records no payment confirmations.
 */
export function getNextScheduledPayment(schedule: ComputedSchedule, asOfDate: ISODateString): ScheduleMonth | undefined {
  return schedule.months.find((m) => isAfter(m.emiDate, asOfDate));
}

/**
 * ============================================================================
 * MAIN ENGINE: computeSchedule()
 * ============================================================================
 *
 * Walks the loan's life one calendar day at a time from the earliest
 * relevant date (loan.loanStartDate, or the first disbursement's date if
 * earlier/loanStartDate is unset) forward to closure or `asOfDate`.
 * Each day resolves outstanding principal (disbursements + financed
 * charges − principal reductions, with any BalanceCorrectionEvent acting
 * as a hard reset from its effective date), the applicable rate, and
 * daily interest = principal × rate / dayCountDivisor. Consecutive days
 * roll up into cycles bounded by EMI due dates.
 *
 * REPAYMENT BEHAVIOUR (see resolveRepaymentArrangement): EMI dates before the
 * regular-EMI start are PRE-EMI (interest-only, principal unchanged);
 * EMI dates on or after it are REGULAR (principal + interest). The start is
 * a saved date or an automatic working assumption — never a hidden fact.
 *  - Under the explicit setting 'capitalize_at_emi_start' pre-EMI interest
 *    is deferred, then added to principal at commencement.
 *  - Calculated rows are never recorded payments; `currentRecordedPrincipal`
 *    follows recorded events only.
 *
 * CYCLE BOUNDARY CONVENTION: a cycle between two due dates accrues
 * interest for the days [previous due date, due date) — the due date is
 * the settlement boundary, not an accruing day. E.g. 15-Oct to 7-Nov is
 * 23 days. A disbursement accrues from its own effective date.
 *
 * REGULAR EMI RESOLUTION (see resolveRegularEmi below): recorded EMI →
 * original full-loan EMI, only if disbursed-to-date has reached the
 * approved amount → otherwise an ASSUMED EMI re-derived over the
 * instalments remaining to contractual maturity (labelled as such).
 */
export function computeSchedule(options: ComputeScheduleOptions): ComputedSchedule {
  const { loan, events } = options;
  const sorted = sortEvents(events);

  // actual_365_25 previously divided by 365 (mislabelled); it now honours its name.
  const dayCountDivisor =
    loan.dayCountConvention === 'actual_360' ? 360 : loan.dayCountConvention === 'actual_365_25' ? 365.25 : 365;

  const firstDisbursementDate = sorted.disbursements[0]?.effectiveDate;
  let walkStart = loan.loanStartDate ?? firstDisbursementDate;
  if (loan.loanStartDate && firstDisbursementDate && isBefore(firstDisbursementDate, loan.loanStartDate)) {
    walkStart = firstDisbursementDate;
  }

  const arrangement = resolveRepaymentArrangement(loan, events);
  const regularEmiStartDate = arrangement.startDate;

  if (!walkStart) {
    return {
      months: [],
      summary: {
        totalEmiPaid: 0,
        totalPreEmiPaid: 0,
        totalInterestPaid: 0,
        totalPrincipalRepaid: 0,
        totalPrepayments: 0,
        totalDisbursed: 0,
        currentOutstandingPrincipal: 0,
        currentRecordedPrincipal: 0,
        isLoanClosed: false,
        currentApplicableAnnualRatePercent: loan.originalAnnualRatePercent,
        currentApplicableEmi: 0,
        currentPhase: 'pre_emi',
        arrangement,
        preEmiInterestCapitalised: 0,
        unpaidInterestCarried: 0,
        projection: { status: 'unavailable', unavailableReason: 'no_disbursement', assumptions: [] },
      },
    };
  }

  // Safety cap so a pathological input can never loop forever.
  const maxWalkEnd = addMonthsClamped(walkStart, loan.originalTenureMonths + 60);
  const walkEnd = options.asOfDate ? (isBefore(options.asOfDate, maxWalkEnd) ? options.asOfDate : maxWalkEnd) : maxWalkEnd;

  const maturity = getContractualMaturity(loan, events);
  const days: ScheduleDay[] = [];
  const months: ScheduleMonth[] = [];

  /**
   * PRINCIPAL TRACKING MODEL: `runningPrincipal` is updated by SAME-DAY
   * DELTAS ONLY (disbursements, financed charges, prepayments), each
   * applied once on its effective day; instalment principal components
   * are subtracted once when their cycle closes. A BalanceCorrectionEvent
   * overwrites it outright on its effective day. A correction never adds
   * to the disbursed total — disbursements are counted from disbursement
   * events only.
   */
  let runningPrincipal = 0;
  // Principal per ACTUAL recorded events only (never reduced by scheduled instalments).
  let recordedPrincipal = 0;
  let carriedUnpaidInterest = 0;
  let preEmiInterestCapitalised = 0;
  let currentDueDate: ISODateString | undefined = firstEmiDateStrictlyAfter(
    walkStart,
    resolveEmiDayOfMonthForDay(walkStart, loan, sorted)
  );

  let cumulativeInterest = 0;
  let cumulativePrincipal = 0;
  let cumulativeTotalPayment = 0;
  let cumulativePrepaymentTotal = 0;
  let isLoanClosed = false;
  let closureDate: ISODateString | undefined;

  // Cycle buffers.
  let cycleInterestAccrued = 0;
  let cycleOpeningBalance = 0;
  let cyclePrepaymentAccrued = 0;
  let cycleDisbursed = 0;
  let cycleSourceEventIds = new Set<UUID>();
  let cycleIsReconciled = false;
  let cycleReconciledFromEventId: UUID | undefined;

  // Assumed-EMI state (only used when no recorded EMI exists and the loan is not fully disbursed).
  let derivedEmiCache: number | undefined;
  let derivedStale = true;
  let lastDerivation: { emi: number; instalments: number } | undefined;
  let lastRegularEmi: { amount: number; basis: RegularEmiBasis } | undefined;
  const basesUsed = new Set<RegularEmiBasis>();

  /**
   * The phase of the cycle ENDING on due date `d`, per the repayment
   * arrangement. Regular EMI applies from regularEmiStartDate inclusive:
   * a due date on or after it is a regular instalment.
   */
  const phaseForDueDate = (d: ISODateString): RepaymentPhase => {
    // Nothing is disbursed yet on this date: nothing can be amortised.
    if (totalDisbursedUpTo(d, sorted) === 0) return 'pre_emi';
    return regularEmiStartDate === undefined || isAfterOrEqual(d, regularEmiStartDate) ? 'regular' : 'pre_emi';
  };

  /** Standard EMI formula on `principal` over the instalments from `dueDate` to contractual maturity, rounded per loan rule. */
  function deriveAssumedEmi(principal: number, ratePercent: number, dueDate: ISODateString): number | undefined {
    if (!maturity) return undefined;
    const n = instalmentsRemainingFrom(dueDate, maturity.date);
    const raw = calculateEmi(principal, ratePercent, n);
    if (raw === undefined) return undefined;
    return applyEmiRounding(raw, loan.emiRoundingRule);
  }

  /**
   * Regular EMI resolution — NEVER silently applies the full-loan EMI to a
   * partially disbursed principal:
   *  1. recorded EMI (event consequence, or Loan.regularEmiAmount);
   *  2. else the original full-loan EMI, only if disbursements to date
   *     have reached the approved amount;
   *  3. else an ASSUMED EMI re-derived over the remaining contractual
   *     instalments, recomputed only when principal rose through a new
   *     disbursement/financed charge (frozen otherwise, as a real EMI is).
   */
  function resolveRegularEmi(
    day: ISODateString,
    principal: number,
    dueDate: ISODateString,
    useCache: boolean
  ): { amount: number; basis: RegularEmiBasis } {
    const explicit = resolveExplicitEmi(day, loan, sorted);
    if (explicit) return explicit;
    if (totalDisbursedUpTo(day, sorted) >= loan.approvedAmount - 0.005) {
      return { amount: loan.originalEmiAtFullDisbursement, basis: 'original_full_disbursement' };
    }
    const rate = resolveRateForDay(addDays(dueDate, -1), loan, sorted);
    let emi: number | undefined;
    if (useCache) {
      if (derivedStale || derivedEmiCache === undefined) {
        derivedEmiCache = deriveAssumedEmi(principal, rate, dueDate);
        derivedStale = false;
        if (derivedEmiCache !== undefined && maturity) {
          lastDerivation = { emi: derivedEmiCache, instalments: instalmentsRemainingFrom(dueDate, maturity.date) };
        }
      }
      emi = derivedEmiCache;
    } else {
      emi = deriveAssumedEmi(principal, rate, dueDate);
    }
    return { amount: emi ?? 0, basis: 'derived_over_contract_tenure' };
  }

  /**
   * Closes the cycle ending on `dueDate` using what accumulated from the
   * days strictly before it. Handles both phases, pushes the
   * ScheduleMonth, updates cumulative totals and resets the buffers.
   */
  function closeCycle(dueDate: ISODateString): void {
    const cyclePhase = phaseForDueDate(dueDate);
    const accrued = applyEmiRounding(cycleInterestAccrued, loan.emiRoundingRule);
    const balanceBefore = runningPrincipal;

    let phase: RepaymentPhase = cyclePhase;
    let payment = 0;
    let interestComponent = 0;
    let principalComponent = 0;
    let isResidual = false;
    let basis: RegularEmiBasis | undefined;
    let closingBalance = balanceBefore;

    if (cyclePhase === 'pre_emi') {
      if (loan.preEmiInterestTreatment === 'capitalize_at_emi_start') {
        // Explicit rule: nothing payable; interest is deferred (tracked as unpaid, NOT paid).
        carriedUnpaidInterest += accrued;
      } else {
        // Interest-only, pay-as-you-go. Principal unchanged.
        payment = accrued;
        interestComponent = accrued;
      }
    } else {
      phase = 'regular';
      const regular = resolveRegularEmi(dueDate, balanceBefore, dueDate, true);
      basis = regular.basis;
      lastRegularEmi = regular;
      basesUsed.add(regular.basis);
      const emiAmount = applyEmiRounding(regular.amount, loan.emiRoundingRule);

      const interestDue = accrued + carriedUnpaidInterest;
      const maxPossiblePayment = balanceBefore + interestDue;
      payment = Math.min(emiAmount, Math.max(0, maxPossiblePayment));
      // Residual closing payment: ONLY when the normally resolved EMI was genuinely reduced by the balance cap.
      isResidual = emiAmount > maxPossiblePayment;

      interestComponent = Math.min(interestDue, payment);
      principalComponent = applyEmiRounding(payment - interestComponent, loan.emiRoundingRule);
      if (principalComponent < 0) principalComponent = 0;
      if (principalComponent > balanceBefore) principalComponent = balanceBefore;

      // Interest the payment did not cover stays UNPAID and is carried forward — never treated as paid.
      carriedUnpaidInterest = Math.max(0, interestDue - interestComponent);
      closingBalance = balanceBefore - principalComponent;
      if (closingBalance <= 0.01 && carriedUnpaidInterest <= 0.01) {
        closingBalance = 0;
        carriedUnpaidInterest = 0;
        isLoanClosed = true;
        closureDate = dueDate;
      }
    }
    runningPrincipal = closingBalance;

    // A pre-EMI cycle with nothing at all in it (before the first disbursement) is not a payment row.
    const isEmptyPreEmiCycle =
      phase === 'pre_emi' &&
      accrued === 0 &&
      balanceBefore === 0 &&
      cycleDisbursed === 0 &&
      cyclePrepaymentAccrued === 0 &&
      !cycleIsReconciled;

    cumulativeInterest += interestComponent;
    cumulativePrincipal += principalComponent;
    cumulativePrepaymentTotal += cyclePrepaymentAccrued;
    cumulativeTotalPayment += payment + cyclePrepaymentAccrued;

    let status: FigureStatus = cycleIsReconciled ? 'reconciled' : 'projected';
    if (!cycleIsReconciled) {
      const statuses: FigureStatus[] = [];
      for (const rc of sorted.rateChanges) {
        if (cycleSourceEventIds.has(rc.id)) statuses.push(rc.repaymentConsequence.status);
      }
      for (const p of sorted.prepayments) {
        if (cycleSourceEventIds.has(p.id) && p.repaymentConsequence) statuses.push(p.repaymentConsequence.status);
      }
      if (statuses.length > 0 && statuses.every((s) => s === 'actual_lic')) {
        status = 'actual_lic';
      }
    }

    if (!isEmptyPreEmiCycle) {
      months.push({
        emiDate: dueDate,
        phase,
        openingBalance: cycleOpeningBalance,
        closingBalance,
        emiAmount: payment,
        interestComponent,
        accruedInterest: accrued,
        principalComponent,
        prepaymentAmount: cyclePrepaymentAccrued,
        disbursedInCycle: cycleDisbursed,
        unpaidInterestCarriedForward: carriedUnpaidInterest,
        regularEmiBasis: basis,
        cumulativeInterest,
        cumulativePrincipal,
        cumulativeTotalPayment,
        isReconciled: cycleIsReconciled,
        reconciledFromEventId: cycleReconciledFromEventId,
        sourceEventIds: Array.from(cycleSourceEventIds),
        status,
        isResidualClosingPayment: isResidual,
      });
    }

    // Next cycle starts on dueDate itself.
    currentDueDate = isLoanClosed
      ? undefined
      : firstEmiDateStrictlyAfter(dueDate, resolveEmiDayOfMonthForDay(dueDate, loan, sorted));

    // EXPLICIT capitalisation rule only: when the next cycle is the first regular one, deferred pre-EMI interest joins principal.
    if (
      cyclePhase === 'pre_emi' &&
      loan.preEmiInterestTreatment === 'capitalize_at_emi_start' &&
      currentDueDate !== undefined &&
      phaseForDueDate(currentDueDate) === 'regular' &&
      carriedUnpaidInterest > 0
    ) {
      runningPrincipal += carriedUnpaidInterest;
      preEmiInterestCapitalised += carriedUnpaidInterest;
      carriedUnpaidInterest = 0;
      derivedStale = true;
    }

    cycleOpeningBalance = runningPrincipal;
    cycleInterestAccrued = 0;
    cyclePrepaymentAccrued = 0;
    cycleDisbursed = 0;
    cycleSourceEventIds = new Set();
    cycleIsReconciled = false;
    cycleReconciledFromEventId = undefined;
  }

  const includeDailyBreakdown = options.includeDailyBreakdown ?? false;
  let day = walkStart;

  while (isBeforeOrEqual(day, walkEnd) && !isLoanClosed) {
    const driversToday: UUID[] = [];

    // --- Same-day disbursements and financed charges (delta). Not repayments. ---
    for (const d of sorted.disbursements) {
      if (d.effectiveDate === day) {
        runningPrincipal += d.amount;
        recordedPrincipal += d.amount;
        cycleDisbursed += d.amount;
        derivedStale = true;
        cycleSourceEventIds.add(d.id);
        driversToday.push(d.id);
      }
    }
    for (const c of sorted.charges) {
      if (c.effectiveDate === day && c.financedIntoPrincipal) {
        runningPrincipal += c.amount;
        recordedPrincipal += c.amount;
        cycleDisbursed += c.amount;
        derivedStale = true;
        cycleSourceEventIds.add(c.id);
        driversToday.push(c.id);
      }
    }

    // --- Same-day genuine principal prepayments (delta) ---
    for (const p of sorted.prepayments) {
      if (p.effectiveDate === day && p.paymentType === 'principal_prepayment') {
        const amt = Math.min(p.amount, runningPrincipal);
        runningPrincipal -= amt;
        recordedPrincipal = Math.max(0, recordedPrincipal - amt);
        cyclePrepaymentAccrued += amt;
        cycleSourceEventIds.add(p.id);
        driversToday.push(p.id);
      }
    }

    for (const rc of sorted.rateChanges) {
      if (rc.effectiveDate === day) {
        cycleSourceEventIds.add(rc.id);
        driversToday.push(rc.id);
      }
    }

    // --- Same-day balance correction (hard overwrite; NOT a disbursement) ---
    for (const bc of sorted.balanceCorrections) {
      if (bc.effectiveDate === day) {
        runningPrincipal = bc.licConfirmedBalance;
        recordedPrincipal = bc.licConfirmedBalance;
        cycleIsReconciled = true;
        cycleReconciledFromEventId = bc.id;
        cycleSourceEventIds.add(bc.id);
        driversToday.push(bc.id);
      }
    }

    if (runningPrincipal < 0) runningPrincipal = 0;

    if (day === walkStart) cycleOpeningBalance = runningPrincipal;

    const rate = resolveRateForDay(day, loan, sorted);

    // Close the cycle BEFORE accruing today's interest (today starts the next cycle).
    if (currentDueDate && day === currentDueDate) {
      closeCycle(day);
    }

    const dailyInterest = (runningPrincipal * rate) / 100 / dayCountDivisor;
    cycleInterestAccrued += dailyInterest;

    if (includeDailyBreakdown) {
      days.push({
        date: day,
        outstandingPrincipal: runningPrincipal,
        applicableAnnualRatePercent: rate,
        dailyInterestAccrued: dailyInterest,
        drivingEventIds: driversToday,
      });
    }

    day = addDays(day, 1);
  }

  const totalEmiPaid = months.reduce((sum, m) => sum + m.emiAmount, 0);
  const totalPreEmiPaid = months.filter((m) => m.phase === 'pre_emi').reduce((sum, m) => sum + m.emiAmount, 0);
  const totalPrincipalRepaid = months.reduce((sum, m) => sum + m.principalComponent + m.prepaymentAmount, 0);

  const currentApplicableAnnualRatePercent = resolveRateForDay(walkEnd, loan, sorted);

  // Regular EMI "as of" the walk's end: recorded → last used → hypothetical (clearly basis-tagged).
  let currentApplicableEmi = 0;
  let regularEmiBasis: RegularEmiBasis | undefined;
  if (!isLoanClosed) {
    const explicit = resolveExplicitEmi(walkEnd, loan, sorted);
    if (explicit) {
      currentApplicableEmi = explicit.amount;
      regularEmiBasis = explicit.basis;
    } else if (lastRegularEmi) {
      currentApplicableEmi = lastRegularEmi.amount;
      regularEmiBasis = lastRegularEmi.basis;
    } else {
      const firstRegularDue = regularEmiStartDate
        ? firstEmiDateOnOrAfter(regularEmiStartDate, resolveEmiDayOfMonthForDay(regularEmiStartDate, loan, sorted))
        : currentDueDate;
      const hypotheticalDue = firstRegularDue && currentDueDate && isAfter(currentDueDate, firstRegularDue) ? currentDueDate : firstRegularDue;
      if (hypotheticalDue && runningPrincipal > 0) {
        const hypothetical = resolveRegularEmi(walkEnd, runningPrincipal, hypotheticalDue, false);
        currentApplicableEmi = hypothetical.amount;
        regularEmiBasis = hypothetical.basis;
      }
    }
  }

  // Phase of the NEXT payment due after the end of this walk.
  const currentPhase: 'pre_emi' | 'regular' | 'closed' = isLoanClosed
    ? 'closed'
    : currentDueDate
      ? phaseForDueDate(currentDueDate)
      : 'regular';

  const totalDisbursed = totalDisbursedUpTo(walkEnd, sorted);
  const totalDisbursedEver = totalDisbursedUpTo('9999-12-31', sorted);

  // --- Projection honesty: a closure date exists only under an identifiable scenario. ---
  let projection: ProjectionInfo;
  if (sorted.disbursements.length === 0) {
    projection = { status: 'unavailable', unavailableReason: 'no_disbursement', assumptions: [] };
  } else if (!isLoanClosed && !options.asOfDate) {
    projection = { status: 'unavailable', unavailableReason: 'does_not_amortise', assumptions: [] };
  } else {
    const assumptions: ProjectionAssumption[] = [
      { code: 'arrangement', mode: arrangement.mode, source: arrangement.source, startDate: regularEmiStartDate, startDateSource: arrangement.startDateSource, firstRegularDueDate: regularEmiStartDate ? firstEmiDateOnOrAfter(regularEmiStartDate, resolveEmiDayOfMonthForDay(regularEmiStartDate, loan, sorted)) : undefined },
    ];
    const undisbursed = loan.approvedAmount - totalDisbursedEver;
    if (undisbursed > 0.5) assumptions.push({ code: 'undisbursed_never_disbursed', amount: undisbursed });
    if (basesUsed.has('recorded_event') || basesUsed.has('recorded_loan')) assumptions.push({ code: 'emi_recorded' });
    if (basesUsed.has('original_full_disbursement')) assumptions.push({ code: 'emi_original_full_disbursement' });
    if (basesUsed.has('derived_over_contract_tenure') && lastDerivation && maturity) {
      assumptions.push({
        code: 'emi_derived_over_contract_tenure',
        emi: lastDerivation.emi,
        instalments: lastDerivation.instalments,
        maturityDate: maturity.date,
        maturitySource: maturity.source,
      });
    }
    assumptions.push({ code: 'rate_unchanged', ratePercent: currentApplicableAnnualRatePercent });
    assumptions.push({ code: 'no_further_prepayments' });
    assumptions.push({ code: 'payments_on_due_dates' });
    assumptions.push({ code: 'day_count', convention: loan.dayCountConvention });
    projection = { status: 'modelled', assumptions };
  }

  const projectionAvailable = projection.status === 'modelled';

  return {
    months,
    days: includeDailyBreakdown ? days : undefined,
    summary: {
      totalEmiPaid,
      totalPreEmiPaid,
      totalInterestPaid: cumulativeInterest,
      totalPrincipalRepaid,
      totalPrepayments: cumulativePrepaymentTotal,
      totalDisbursed,
      currentOutstandingPrincipal: isLoanClosed ? 0 : runningPrincipal,
      currentRecordedPrincipal: recordedPrincipal,
      projectedClosureDate: projectionAvailable ? closureDate : undefined,
      projectedTotalInterest: isLoanClosed && projectionAvailable ? cumulativeInterest + preEmiInterestCapitalised : undefined,
      projectedTotalPayment: isLoanClosed && projectionAvailable ? cumulativeTotalPayment : undefined,
      isLoanClosed,
      currentApplicableAnnualRatePercent,
      currentApplicableEmi,
      regularEmiBasis,
      currentPhase,
      nextDueDate: isLoanClosed ? undefined : currentDueDate,
      firstRegularEmiDueDate: regularEmiStartDate
        ? firstEmiDateOnOrAfter(regularEmiStartDate, resolveEmiDayOfMonthForDay(regularEmiStartDate, loan, sorted))
        : undefined,
      arrangement,
      preEmiInterestCapitalised,
      unpaidInterestCarried: carriedUnpaidInterest,
      projection,
    },
  };
}

/**
 * Counts only genuine (non-residual) REGULAR cycles in a computed
 * schedule. Interest-only pre-EMI rows are never tenure cycles, and a
 * mechanically-generated residual closing payment is not a scheduled
 * tenure cycle either. This is the single canonical definition of "how
 * many real EMI cycles has/will this schedule have" — reused by
 * savingsBaselines.ts and by Home/Schedule/Report.
 */
export function countGenuineCycles(schedule: ComputedSchedule): number {
  return schedule.months.filter(isGenuineRegularCycle).length;
}

/**
 * Remaining regular instalments for display: genuine regular cycles in
 * the unrestricted projection that fall after the selected "As of" date.
 * Only meaningful when the projection is 'modelled' — callers must check
 * `projection.status` and show the explanation otherwise.
 */
export function countRemainingGenuineCycles(currentProjection: ComputedSchedule, currentPosition: ComputedSchedule): number {
  const cyclesCompleted = countGenuineCycles(currentPosition);
  const totalGenuineCycles = countGenuineCycles(currentProjection);
  return Math.max(0, totalGenuineCycles - cyclesCompleted);
}

/**
 * The three agreed interest-saved baselines (spec §12 / §6 fixed
 * decision #7). Each is computed by calling computeSchedule() with a
 * different synthetic event list — never separate calculation logic.
 * Implemented in Phase 4 (Analytics), but the shape is fixed now so nothing
 * upstream needs to change later.
 */
export type InterestSavedBaseline =
  | 'original_terms_no_prepayments' // Baseline A — headline "Interest Avoided"
  | 'original_terms_current_rate_no_prepayments' // Baseline B
  | 'current_state_no_future_prepayments'; // Baseline C

export interface BaselineComparison {
  baseline: InterestSavedBaseline;
  label: string;
  description: string;
  baselineTotalInterest: number;
  actualOrProjectedTotalInterest: number;
  interestAvoided: number;
  baselineTenureMonths: number;
  actualOrProjectedTenureMonths: number;
  tenureSavedMonths: number;
}

export const INTEREST_SAVED_BASELINE_DEFINITIONS: Record<
  InterestSavedBaseline,
  { label: string; description: string }
> = {
  original_terms_no_prepayments: {
    label: 'Interest Avoided (vs. Original Terms)',
    description:
      'Compared to paying this loan exactly as originally sanctioned, with no rate changes and no prepayments.',
  },
  original_terms_current_rate_no_prepayments: {
    label: 'Interest Avoided (Prepayment Impact Only)',
    description: 'Isolates the effect of your prepayments from the effect of rate changes.',
  },
  current_state_no_future_prepayments: {
    label: 'Projected Interest If You Stop Prepaying Today',
    description: 'What happens if you stop prepaying from today onward, given your loan as it stands now.',
  },
};

